import React, { useMemo } from 'react';
import { GripVertical } from 'lucide-react';
import InfoButton from '../InfoButton';
import { AVAILABLE_MAP_ZONES } from './constants';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

interface MapCardHeaderProps {
  zonesLoaded: boolean;
  selectedZones: MarketZoneId[];
  activeTab: 'list' | 'map';
  onClearSelections: () => void;
  onTabChange: (tab: 'list' | 'map') => void;
}

const MapCardHeader: React.FC<MapCardHeaderProps> = ({
  zonesLoaded,
  selectedZones,
  activeTab,
  onClearSelections,
  onTabChange,
}) => {
  const selectedSummary = useMemo(() => {
    const count = selectedZones.length;
    if (count === 0) return '';
    const labels = selectedZones
      .slice(0, 3)
      .map((id) => AVAILABLE_MAP_ZONES.find((z) => z.id === id)?.label || id)
      .join(', ');
    const suffix = count > 3 ? ` +${count - 3}` : '';
    return `${labels}${suffix}`;
  }, [selectedZones]);
  return (
    <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 relative">
      {/* Drag Handle */}
      <div className="drag-handle absolute top-2 right-2 p-1 rounded opacity-40 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10">
        <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
      </div>

      <div className="flex items-center justify-between p-3 pb-0 pr-12 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Market Zones
            {zonesLoaded && (
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                ({AVAILABLE_MAP_ZONES.length} zones)
              </span>
            )}
            {selectedZones.length > 0 && (
              <span className="text-xs text-pink-600 dark:text-pink-400 ml-2">
                • {selectedZones.length} selected{selectedSummary ? `: ${selectedSummary}` : ''}
              </span>
            )}
          </h2>
          <InfoButton title="Map card">
            <div>
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">Goal:</span> Select a
                market zone to drive other cards.
              </div>
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">How:</span>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    Click a zone on the{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      Map
                    </span>{' '}
                    or switch to{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      List
                    </span>
                    .
                  </li>
                  <li>
                    Use{' '}
                    <span className="px-1 py-0.5 rounded bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300">
                      Clear
                    </span>{' '}
                    to remove the selection.
                  </li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">Tip:</span> The
                selected zone name appears next to the title.
              </div>
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">Tip:</span> Multiple
                zones can be selected by clicking on them.
              </div>
            </div>
          </InfoButton>
        </div>
        <div className="flex items-center gap-2 pr-8">
          {selectedZones.length > 0 && (
            <button
              onClick={onClearSelections}
              className="text-xs px-2 py-1 text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 hover:bg-pink-50 dark:hover:bg-pink-900/20 rounded transition-colors"
              title="Clear selection"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <button
          onClick={() => onTabChange('map')}
          className={`
            flex-1 px-3 py-2 text-sm font-medium transition-colors relative
            ${
              activeTab === 'map'
                ? 'text-pink-600 dark:text-pink-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }
          `}
        >
          Map
          {activeTab === 'map' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-500"></div>
          )}
        </button>
        <button
          onClick={() => onTabChange('list')}
          className={`
            flex-1 px-3 py-2 text-sm font-medium transition-colors relative
            ${
              activeTab === 'list'
                ? 'text-pink-600 dark:text-pink-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }
          `}
        >
          List
          {activeTab === 'list' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-500"></div>
          )}
        </button>
      </div>
    </div>
  );
};

export default MapCardHeader;
